import {
  getJobLevelById,
  getLevelSkills,
} from '../../common/helpers/company.helper'
import { ICompany } from '../../common/models/Company'
import { IPdfExportParams } from '../../common/models/PdfExport'
import { IPdfAssets } from './buildPdfAssets'

// Opt-in deep probe: `localStorage.setItem('summPdfProbe', '1')`, reload, export.
// The export then renders one item at a time first (see renderPdfProbe), logging
// after each, and the render timeout is lifted. For a HANGING export that's the only
// way to learn where it stops — a frozen render never throws, so nothing else runs.
export const isPdfProbeEnabled = (): boolean => {
  try {
    return window.localStorage.getItem('summPdfProbe') === '1'
  } catch {
    return false
  }
}

// Shareable console trace of a client-side PDF export. The whole render happens in
// the browser, so when an export works for one company and fails for another the
// only evidence lives in that tab's console. Every stage logs one `[PDF]` line and
// is kept in a trace that can be printed as a single copy-pasteable block, so dumps
// from two companies can be diffed.
//
// Nothing here may throw — a diagnostic that crashes the export it describes is
// worse than no diagnostic (hence the `safe()` wrapper on every snapshot).

interface ITraceEntry {
  ms: number
  step: string
  detail?: unknown
}

let trace: ITraceEntry[] = []
let startedAt = 0

const now = () => (typeof performance === 'undefined' ? 0 : performance.now())

const safeJson = (value: unknown): string => {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

// Runs a snapshot builder, returning the error instead of throwing it.
const safe = <T>(build: () => T): T | { snapshotFailed: string } => {
  try {
    return build()
  } catch (error) {
    return { snapshotFailed: String(error) }
  }
}

export const startPdfTrace = (label: string): void => {
  trace = []
  startedAt = now()
  pdfLog(`===== ${label} =====`)
}

// Detail is logged as JSON *text*, not as an object: a render that freezes the tab
// never reaches the full-trace block, and a collapsed `{…}` in the console can't be
// expanded once the tab is frozen — a string is readable and copy-pasteable as-is.
export const pdfLog = (step: string, detail?: unknown): void => {
  const ms = Math.round(now() - startedAt)
  trace.push({ ms, step, detail })
  // eslint-disable-next-line no-console
  console.log(
    `[PDF +${ms}ms] ${step}`,
    detail === undefined ? '' : safeJson(detail)
  )
}

// Times an awaited stage. A hang (asset fetch, @react-pdf render) shows up as a
// "start" line with no matching "done", which is exactly what a timeout hides.
export const pdfStep = async <T>(
  step: string,
  run: () => Promise<T>
): Promise<T> => {
  pdfLog(`${step} → start`)
  try {
    const result = await run()
    pdfLog(`${step} → done`)
    return result
  } catch (error) {
    pdfLog(`${step} → FAILED`, String(error))
    throw error
  }
}

// Runs a stage with a 2s heartbeat. This is what tells the two never-finishing modes
// apart, which need opposite fixes:
//   heartbeat KEEPS ticking → the main thread is alive and the promise is simply
//     pending: an async stall (a font/glyph load that never resolves).
//   heartbeat STOPS at the first tick → a synchronous @react-pdf layout loop has
//     frozen the thread (the wrap={false}-over-a-page footgun).
// A frozen thread also means the export's own setTimeout can never fire, so "no error,
// spins forever" is expected there — absence of the timeout message is not a clue.
export const pdfStepWithHeartbeat = async <T>(
  step: string,
  run: () => Promise<T>
): Promise<T> => {
  const started = now()
  const beat = setInterval(
    () =>
      pdfLog(`${step} → still running`, {
        s: Math.round((now() - started) / 1000),
        thread: 'alive',
      }),
    2000
  )
  try {
    return await pdfStep(step, run)
  } finally {
    clearInterval(beat)
  }
}

// Times one sub-task and logs its duration on completion (no "start" line — used for
// the parallel asset fetches, where a run of start lines would just be noise). A slow
// asset is the usual reason an export that normally works suddenly runs past its
// timeout, so each fetch is timed individually.
export const pdfTimed = async <T>(
  step: string,
  run: () => Promise<T>
): Promise<T> => {
  const started = now()
  const result = await run()
  pdfLog(step, { ms: Math.round(now() - started) })
  return result
}

export const pdfTraceText = (): string =>
  trace
    .map(
      (e) =>
        `+${e.ms}ms ${e.step}${
          e.detail === undefined ? '' : ` ${safeJson(e.detail)}`
        }`
    )
    .join('\n')

// One console block with the whole run — the thing to copy/paste when comparing a
// working company against a failing one.
export const logPdfTrace = (): void => {
  // eslint-disable-next-line no-console
  console.log(`[PDF] full trace\n${pdfTraceText()}`)
}

// An array field that the render code maps over unconditionally: `3` reads as fine,
// `undefined` / `missing` as the reason it blew up in this company only.
const arrayInfo = (value: unknown): number | string =>
  Array.isArray(value)
    ? value.length
    : value === undefined
    ? 'undefined'
    : typeof value

const charCount = (...parts: Array<string | undefined>): number =>
  parts.reduce((total, part) => total + (part ? part.length : 0), 0)

const longestWord = (text?: string): string =>
  (text || '')
    .split(/\s+/)
    .reduce(
      (longest, word) => (word.length > longest.length ? word : longest),
      ''
    )

// Rough pt width of a word (~0.55 em per character, averaged over Helvetica). Only
// needs to be good enough to spot a word that cannot fit its column: hyphenation is
// disabled for PDFs (registerFonts), so an unbreakable overflowing word is a known
// way to make @react-pdf's line breaker spin instead of finishing.
const wordWidthPt = (word: string, fontSize: number): number =>
  Math.round(word.length * fontSize * 0.55)

// Usable text width of the form's item body: A4 minus the page padding minus the
// number-badge indent that every item's content hangs off.
const ITEM_BODY_WIDTH = 595.28 - 2 * 32 - (22 + 8)

// The two evaluation scales, which only the evaluation form renders — one bubble
// column per option (PdfScaleBubbles), so the more options a company configured the
// narrower each label column gets.
export const describeEvaluationScales = (company: ICompany) =>
  safe(() => {
    const evaluations = company.settings?.evaluations
    const describe = (
      name: string,
      options: unknown
    ): Record<string, unknown> => {
      if (!Array.isArray(options)) return { name, options: arrayInfo(options) }
      const columnPt = options.length
        ? Math.round(ITEM_BODY_WIDTH / options.length)
        : 0
      return {
        name,
        options: options.length,
        columnPt,
        labels: options.map((o: any) => ({
          score: o?.score,
          label: o?.label,
          // Label font size in PdfScaleBubbles is 8pt; a longest word wider than its
          // column can't be broken (hyphenation off) — the hang-risk case.
          overflowPt: wordWidthPt(longestWord(o?.label), 8) - (columnPt - 4),
        })),
        nonNumericScores: options.filter(
          (o: any) => typeof o?.score !== 'number'
        ).length,
      }
    }
    return {
      skillScale: describe('skillScores', evaluations?.skillScores),
      cultureScale: describe('cultureScores', evaluations?.cultureScores),
    }
  })

// Per-item render plan: what each skill / culture block contains and how the form
// will lay it out. An item the page renders atomically (wrap={false}) that turns out
// taller than a page, or an unbreakable word wider than the column, are the two
// documented ways this render hangs instead of failing.
export const describeItemsForPdf = (
  company: ICompany,
  params: IPdfExportParams
) =>
  safe(() => {
    const jobLevelId = String(params.jobLevelId || '')
    const { level } = jobLevelId
      ? getJobLevelById(jobLevelId, company)
      : { level: undefined }
    const cultureRankId = level?.cultureRankId

    const describe = (
      kind: string,
      index: number,
      title: string,
      body: string
    ) => {
      const word = longestWord(body)
      const lines = body ? body.split('\n').filter((l) => l.trim()) : []
      // Markdown keeps every single newline as its own line (see the Markdown atom),
      // so item height is driven by LINE COUNT, not character count: many short
      // bullet lines are far taller than the same characters in one paragraph.
      const renderedLines = lines.reduce(
        (total, line) =>
          total +
          Math.max(1, Math.ceil(wordWidthPt(line, 10) / ITEM_BODY_WIDTH)),
        0
      )
      // ~12.5pt per rendered line, + the item header (~40pt) + the bounded score
      // tail (~140pt: scale row, explanation label, two write-lines).
      const estimatedPt = Math.round(renderedLines * 12.5 + 40 + 140)
      const atomic = body.length <= 1000
      return {
        kind,
        index,
        title: (title || '').slice(0, 40),
        chars: body.length,
        // Mirrors isCompactItem in EvaluationFormPage.
        atomic,
        lines: lines.length,
        renderedLines,
        estimatedPt,
        // THE hang case: the page renders this item atomically (wrap={false}) but it
        // does not fit a page (~700pt usable), and @react-pdf loops forever on an
        // over-page node it may not split. A char-count threshold misses it because
        // the height comes from the line count.
        atomicOverPage: atomic && estimatedPt > 700,
        longestWordChars: word.length,
        // Body text renders at ~10pt across the full item width.
        wordOverflowPt: wordWidthPt(word, 10) - ITEM_BODY_WIDTH,
        mdImages: (body.match(/!\[/g) || []).length,
        mdTableRows: (body.match(/^\s*\|/gm) || []).length,
      }
    }

    // Same resolution the render uses, so `skill#N` in a report points at the
    // item the export actually places. Filtering the library instead numbered
    // them in record-creation order.
    const skills = getLevelSkills(level, company).map((s, i) =>
      describe(
        'skill',
        i,
        s.name,
        `${s.descriptionShort || ''}\n${s.description || ''}`
      )
    )

    const values = (company.cultureValues || [])
      .filter((v) => !v.inactive)
      .map((v, i) =>
        describe(
          'value',
          i,
          v.name,
          `${v.description || ''}\n${
            (v.levels || []).find(
              (l) => l.cultureRankId + '' === cultureRankId + ''
            )?.description || ''
          }`
        )
      )

    const items = [...skills, ...values]
    return {
      // Read this first: the items that can hang the render, named up front so the
      // per-item list below only needs reading when this is empty.
      hangRisks: items
        .filter((i) => i.atomicOverPage || i.wordOverflowPt > 0)
        .map((i) => `${i.kind}#${i.index} "${i.title}" (${i.estimatedPt}pt)`),
      items,
    }
  })

// Fingerprint of everything the evaluation-form / job-profile render reads off the
// company, shaped for eyeball-diffing two companies: array lengths (not contents),
// presence of the settings blocks, and how the requested job level resolves.
export const describeCompanyForPdf = (
  company: ICompany,
  params: IPdfExportParams
) =>
  safe(() => {
    const evaluations = company.settings?.evaluations
    const pdfSettings = company.settings?.pdf
    const jobLevelId = String(params.jobLevelId || '')
    const { job, level } = jobLevelId
      ? getJobLevelById(jobLevelId, company)
      : { job: undefined, level: undefined }

    const skills = getLevelSkills(level, company)
    const activeValues = (company.cultureValues || []).filter(
      (v) => !v.inactive
    )
    const cultureRankId = level?.cultureRankId
    const companyColors: Record<string, unknown> = company.companyColors || {}

    // Item sizes drive the render's own page-fitting heuristics (isCompactItem,
    // wrap={false}), so an outlier description is a plausible hang/timeout cause.
    const itemSizes = [
      ...skills.map((s) =>
        charCount(s.descriptionShort || '', s.description || '')
      ),
      ...activeValues.map((v) =>
        charCount(
          v.description || '',
          (v.levels || []).find(
            (l) => l.cultureRankId + '' === cultureRankId + ''
          )?.description || ''
        )
      ),
    ]

    return {
      company: {
        name: company.name,
        id: company._id + '',
        hasLogo: !!company.logo,
        language: company.language || '',
      },
      counts: {
        skills: arrayInfo(company.skills),
        cultureValues: arrayInfo(company.cultureValues),
        activeCultureValues: activeValues.length,
        cultureRanks: arrayInfo(company.cultureRanks),
        departments: arrayInfo(company.departments),
        jobs: arrayInfo(company.jobs),
        jobProperties: arrayInfo(company.settings?.jobProperties),
        companyColorKeys: Object.keys(companyColors).length,
      },
      // The evaluation form is the only export that needs these two scales, so a
      // company missing them fails here and nowhere else.
      evaluationScales: {
        hasEvaluationSettings: !!evaluations,
        skillScores: arrayInfo(evaluations?.skillScores),
        cultureScores: arrayInfo(evaluations?.cultureScores),
      },
      pdfSettings: {
        hasPdfSettings: !!pdfSettings,
        display: pdfSettings?.display || null,
        evaluationDisplay: pdfSettings?.evaluationDisplay || null,
        bodyFont: pdfSettings?.bodyFont
          ? {
              name: pdfSettings.bodyFont.name,
              hasUrl: !!pdfSettings.bodyFont.url,
            }
          : null,
        headingFont: pdfSettings?.headingFont
          ? {
              name: pdfSettings.headingFont.name,
              hasUrl: !!pdfSettings.headingFont.url,
            }
          : null,
      },
      level: {
        jobLevelId,
        found: !!level,
        jobTitle: job?.title || '',
        levelsInJob: arrayInfo(job?.levels),
        levelTitle: level?.title || '',
        skillsIds: arrayInfo(level?.skillsIds),
        matchedSkills: skills.length,
        cultureRankId: cultureRankId ? cultureRankId + '' : '',
        cultureRankFound: !!(company.cultureRanks || []).find(
          (r) => r._id + '' === cultureRankId + ''
        ),
        levelProperties: arrayInfo(level?.properties),
      },
      // Shapes the render walks without guarding — a missing array here is a crash,
      // a missing colour key only a silent fallback.
      dataShape: {
        valuesWithoutLevelsArray: activeValues.filter(
          (v) => !Array.isArray(v.levels)
        ).length,
        valueColorKeysMissing: activeValues
          .map((v) => v.color)
          .filter((key) => !key || !companyColors[key]),
      },
      content: {
        totalChars: itemSizes.reduce((a, b) => a + b, 0),
        longestItemChars: itemSizes.length ? Math.max(...itemSizes) : 0,
        // Items over the render's 1000-char compact threshold take the split-safe
        // (wrapping) path instead of the atomic one.
        itemsOver1000Chars: itemSizes.filter((n) => n > 1000).length,
      },
    }
  })

// The declared MIME of a data URI. @react-pdf decodes only JPEG and PNG images;
// `toPdfImageDataUri` re-encodes anything else to PNG before it gets here, so a
// non-png/jpeg value in the trace means that conversion was skipped or failed.
const dataUriMime = (uri?: string): string =>
  (uri || '').startsWith('data:')
    ? (uri as string).slice(5, (uri as string).indexOf(';')) || 'none'
    : uri
    ? 'not-a-data-uri'
    : 'none'

// Real container format of a font, read from its first bytes rather than its filename.
// A `.otf` name says nothing: a woff/woff2 (or an HTML error page served with a 200)
// renamed to .otf reaches fontkit as an unparseable blob, and `OTTO` (CFF outlines)
// exercises a different fontkit path than a plain TrueType.
const fontFormat = (uri?: string): string => {
  try {
    const base64 = (uri || '').split(',')[1]
    if (!base64) return 'none'
    const head = atob(base64.slice(0, 12))
    const tag = head.slice(0, 4)
    if (tag === 'wOFF') return 'woff (fontkit cannot parse)'
    if (tag === 'wOF2') return 'woff2 (fontkit cannot parse)'
    if (tag === 'OTTO') return 'otf-cff'
    if (tag === 'ttcf') return 'ttc'
    if (tag === 'true' || tag === 'typ1') return 'ttf'
    const code = head.charCodeAt(0)
    if (code === 0 && head.charCodeAt(1) === 1) return 'ttf'
    if (head.trim().startsWith('<')) return 'html/xml — NOT a font'
    return `unknown (${tag})`
  } catch {
    return 'unreadable'
  }
}

// Sizes and real formats of the inlined assets. A huge department image or font is the
// usual reason a render runs past the export timeout; a format the renderer cannot
// decode is the usual reason it misbehaves for one company only.
export const describePdfAssets = (assets: IPdfAssets) =>
  safe(() => {
    const kb = (uri?: string) => Math.round((uri || '').length / 1024)
    const images = Object.values(assets.departmentImages || {})
    return {
      logoKb: kb(assets.logo),
      logoMime: dataUriMime(assets.logo),
      bodyFont: assets.bodyFont
        ? {
            name: assets.bodyFont.name,
            kb: kb(assets.bodyFont.dataUri),
            format: fontFormat(assets.bodyFont.dataUri),
          }
        : null,
      headingFont: assets.headingFont
        ? {
            name: assets.headingFont.name,
            kb: kb(assets.headingFont.dataUri),
            format: fontFormat(assets.headingFont.dataUri),
          }
        : null,
      departmentImageMimes: images.map(dataUriMime),
      departmentImages: images.length,
      departmentImagesKb: images.reduce((total, uri) => total + kb(uri), 0),
      largestDepartmentImageKb: images.length
        ? Math.max(...images.map((uri) => kb(uri)))
        : 0,
      departmentIcons: Object.keys(assets.departmentIcons || {}).length,
    }
  })

// What the builder actually produced, which is what the document then renders. The
// shape is read defensively so this covers every export type in the registry.
export const describePdfData = (data: any) =>
  safe(() => ({
    title: data?.jobLevelTitle ?? data?.memberName ?? '',
    skills: arrayInfo(data?.skills),
    cultureValues: arrayInfo(data?.cultureValues),
    // Evaluation form only: empty scales mean the form draws blank write-lines
    // instead of the company's tick rows.
    skillScale: arrayInfo(data?.skillScale),
    cultureScale: arrayInfo(data?.cultureScale),
    headerChips: arrayInfo(data?.headerChips),
    properties: arrayInfo(data?.properties),
    culturalPhase: data?.culturalPhase || '',
    hasDepartmentImage: !!data?.departmentImage,
    departmentColor: data?.departmentColor || '',
    display: data?.display || null,
    colors: data?.colors || null,
  }))

// Labels i18next couldn't resolve — it echoes the key back, so the value still looks
// like a key ("evaluationFormPdf.title"). Those render as raw keys in the PDF, and
// differ per company when a custom translation file is loaded.
export const untranslatedLabels = (labels: Record<string, any>): string[] =>
  Object.values(labels || {}).filter(
    (value) =>
      typeof value === 'string' && /^[a-zA-Z][\w]*(\.[\w]+)+$/.test(value)
  )
